import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Users, Award, Target, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Headerimg from "../../Assests/headerImg.jpg";

const HeroSection = () => {
  const navigate = useNavigate();
  
  const handleLearnMoreClick = () => {
    navigate('/contactus');
  };

  const stats = [
    { icon: Users, value: "100+", label: "Happy Clients" },
    { icon: Award, value: "200+", label: "Projects Completed" },
    { icon: Target, value: "8+", label: "Years Experience" },
    { icon: Star, value: "98%", label: "Success Rate" },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden pt-16 sm:pt-20">
      {/* Enhanced decorative elements */}
      <div className="absolute top-20 left-4 sm:left-10 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-lumbini-blue/10 to-lumbini-cyan/10 rounded-full opacity-60"></div>
      <div className="absolute top-40 right-4 sm:right-20 w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-lumbini-cyan/15 to-accent-400/10 rounded-full opacity-50"></div>
      <div className="absolute bottom-20 left-1/4 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-lumbini-blue/20 to-lumbini-cyan/15 rounded-full opacity-40"></div>
      
      {/* Additional subtle background elements */}
      <div className="absolute top-1/3 right-1/4 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-lumbini-cyan/5 to-transparent rounded-full opacity-30"></div>
      <div className="absolute bottom-1/3 left-1/3 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-lumbini-blue/8 to-transparent rounded-full opacity-25"></div>
      <div className="absolute top-1/2 left-1/6 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-accent-400/10 to-lumbini-cyan/5 rounded-full opacity-35"></div>
      
      {/* Subtle geometric shapes */}
      <div className="absolute top-32 right-1/3 w-4 h-4 bg-lumbini-blue/20 rotate-45 opacity-40"></div>
      <div className="absolute bottom-32 left-1/5 w-6 h-6 bg-lumbini-cyan/15 rotate-12 opacity-30"></div>
      <div className="absolute top-1/4 left-1/2 w-3 h-3 bg-accent-400/20 rotate-45 opacity-50"></div>
      
      <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col min-h-screen">
          {/* Main Content Area - Centered */}
          <div className="flex-1 flex items-center justify-center">
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center w-full">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="text-secondary-900 space-y-6"
              >
                {/* Main Heading */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="space-y-2 text-center lg:text-left"
                >
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight text-gray-900">
                    Consulting Tailored for Success
                  </h1>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                    <span className="text-gradient bg-gradient-to-r from-lumbini-blue via-lumbini-cyan to-accent-500 bg-clip-text text-transparent">
                      By Lumbini
                    </span>
                  </h1>
                </motion.div>

                {/* Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="pt-4 sm:pt-6"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        className="text-center group"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <div className="bg-white rounded-xl p-3 sm:p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                          <div className="flex justify-center mb-2 sm:mb-4">
                            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-lumbini-blue/80 to-lumbini-cyan/80 rounded-xl flex items-center justify-center">
                              <stat.icon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                            </div>
                          </div>
                          <div className="text-lg sm:text-2xl font-bold text-gray-900 mb-1">
                            {stat.value}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 font-medium">
                            {stat.label}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Action Buttons - Fixed at Bottom */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pb-8"
                >
                  
                  <button 
                    onClick={handleLearnMoreClick}
                    className="group relative flex items-center justify-center px-6 sm:px-8 py-4 bg-white border-2 border-lumbini-blue text-lumbini-blue font-semibold rounded-xl hover:bg-lumbini-blue hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 h-[56px] overflow-hidden w-full sm:w-auto">
                    {/* Background gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-lumbini-blue/90 to-lumbini-cyan/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <span className="relative z-10 flex items-center">
                 Learn More  
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </button>
                </motion.div>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="relative flex justify-center lg:justify-end"
              >
                <div className="relative group w-full max-w-lg h-[550px] sm:h-[850px] lg:h-[950px] xl:h-[550px]">
                  <img
                    className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500 rounded-2xl"
                    src={Headerimg}
                    alt="Lumbini Consultancy Services"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;

